import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { useLayoutEffect } from "react";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { installClientErrorCapture } from "@/lib/clientErrorCapture";
import { isFullwidthPath } from "@/lib/fullwidth-routes";
import {
  renderBootFallback,
  installChunkLoadRecovery,
  checkDeploymentAndRecover,
} from "@/lib/startupRecovery";
import { clearLegacyBackendAuthStorage, validateBackendConfig } from "@/lib/backendConfigGuard";
import App from "./App";
import "./index.css";

try { (window as any).__mainScriptStarted = true; } catch { /* ignore */ }

// Marker rendered ABOVE every async provider. Runs in the commit phase of
// the very first React render, so the boot watchdog stops treating the app
// as "stalled" the moment React mounts — even if PersistQueryClientProvider
// is still restoring the IndexedDB query cache. Using useLayoutEffect makes
// the flag flip before paint, eliminating the previous race where a slow
// IDB restore would let the 20s watchdog overwrite the running UI.
function BootCommitMarker() {
  useLayoutEffect(() => {
    try { (window as any).__appCommitted = true; } catch { /* ignore */ }
  }, []);
  return <span hidden aria-hidden="true" style={{ display: "none" }} />;
}

// Bumped on each release-critical change so the published bundle hash changes
// and stale CDN copies don't trap users on a white screen.
const __BUILD_ID = "lovable-cloud-auth-guard-2026-05-24";
try { console.debug("[boot] build", __BUILD_ID); } catch { /* ignore */ }

try { clearLegacyBackendAuthStorage(); } catch { /* ignore */ }
const backendConfigIssue = validateBackendConfig();

// Install global error listeners ASAP so uncaught errors before React mounts are still captured
try { installClientErrorCapture(); } catch { /* ignore */ }
// Catch chunk-load failures (stale deploy, flaky network) and self-heal once.
try { installChunkLoadRecovery(); } catch { /* ignore */ }

// Apply fullwidth class synchronously before React renders so direct URL
// loads (e.g. /auth in a new tab) don't briefly inherit the phone-frame layout.
if (typeof window !== "undefined") {
  const rootEl = document.getElementById("root");
  if (rootEl && isFullwidthPath(window.location.pathname)) {
    rootEl.classList.add("fullwidth");
  }
}

const mount = () => {
  const container = document.getElementById("root");
  if (!container || (container as any).__reactRoot) return;

  if (backendConfigIssue) {
    try {
      renderBootFallback(container, backendConfigIssue.detail, backendConfigIssue.title);
      (window as any).__bootError = `${backendConfigIssue.title}: ${backendConfigIssue.detail}`;
    } catch { /* ignore */ }
    return;
  }

  try {
    const root = createRoot(container);
    (container as any).__reactRoot = root;
    // Do NOT clear the static loader manually — React will replace the
    // container's children on its first commit. Clearing it here creates a
    // visible blank between the branded loader and React's first paint,
    // which the user perceived as a second loading screen.
    root.render(
      <>
        {/* Marker FIRST and OUTSIDE App so it commits before any provider
            (PersistQueryClientProvider, AuthProvider, lazy routes, Suspense)
            can hold the first render hostage. */}
        <BootCommitMarker />
        <HelmetProvider>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </HelmetProvider>
      </>
    );
    try { (window as any).__appMounted = true; } catch { /* ignore */ }
  } catch (err) {
    try { console.error("[bootstrap] Failed to mount React app", err); } catch { /* ignore */ }
    try {
      (window as any).__bootError = String((err as any)?.stack || err);
    } catch { /* ignore */ }
    try {
      renderBootFallback(
        container,
        "The app couldn't start. Try reloading, or reset local data if the problem keeps happening.",
        (window as any).__bootError,
      );
    } catch { /* ignore */ }
  }
};

// Mount React as quickly as possible. The preview cache wipe still runs
// pre-mount (cheap, in-memory), but the deployment-mismatch HEAD check is
// deferred until AFTER mount so it never blocks first paint. If a mismatch
// is detected, the existing one-shot recovery still reloads the page.
// Mount React IMMEDIATELY. Do not block first paint on storage cleanup —
// if `clearPreviewCaches` ever stalls (corrupt IDB, hung dynamic import,
// blocked SW unregister) it would white-screen the app indefinitely.
// Cleanup runs in the background after mount on preview hosts only.
mount();
setTimeout(() => {
  checkDeploymentAndRecover().catch(() => { /* ignore */ });
}, 2000);
